
import React, { useEffect, useRef, useState } from "react";
import api from "./api";
import { getSocket, connectSocket, disconnectSocket } from "./socket";





/* =========================
   CONSTANTS
========================= */
const MEDIA_BASE_URL =
  import.meta.env.VITE_API_URL || "https://crm.backend.thefither.com/";

/* =========================
   HELPERS
========================= */
const formatTime = (d) =>
  new Date(d).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const AudioMessage = ({ src }) => {
    const ref = React.useRef(null);
  
    React.useEffect(() => {
      if (ref.current) {
        ref.current.load();   // 🔥 force decode
      }
    }, [src]);
  
    return (
      <audio controls ref={ref} className="w-64">
        <source src={src} />
      </audio>
    );
  };
  
const isSameDay = (d1, d2) =>
  d1 &&
  d2 &&
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const getDateLabel = (date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// 🔥 FIX: support createdAt + created_at
const getMessageDate = (msg) =>
  new Date(msg.createdAt || msg.created_at);

const getStatusStyle = (status) => {
  switch (status) {
    case "interested":
      return "bg-yellow-100 text-yellow-700";
    case "converted":
      return "bg-green-100 text-green-700";
    case "lost":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const notificationSound = new Audio("/ok.mp3");
let lastSoundAt = 0;

const handleLogout = () => {
  localStorage.removeItem("token");
  disconnectSocket();
  window.location.href = "/";
};

/* =========================
   MEDIA RENDERER
========================= */

const renderMessageContent = (msg) => {
  const type = msg.message_type || "text"; // ✅ fallback

  switch (type) {

    case "text":
      return <p className="text-sm">{msg.message}</p>;

    case "image":
      return (
        <>
          <img
            src={`${MEDIA_BASE_URL}${msg.media_url}`}
            alt=""   // ✅ FIXED
            className="rounded-lg max-w-xs mb-1"
          />
          {msg.message && (
            <p className="text-sm mt-1">{msg.message}</p>
          )}
        </>
      );

    case "video":
      return (
        <>
          <video
            controls
            className="rounded-lg max-w-xs mb-1"
          >
            <source src={`${MEDIA_BASE_URL}${msg.media_url}`} />
          </video>
          {msg.message && (
            <p className="text-sm mt-1">{msg.message}</p>
          )}
        </>
      );

    case "voice":
    case "audio":
      return (
        <AudioMessage
        src={`${MEDIA_BASE_URL}${msg.media_url}`}
        key={`audio-${msg.id}-${msg.media_url}`}
      />
      );

    case "document":
      return (
        <a
          href={`${MEDIA_BASE_URL}${msg.media_url}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm underline text-blue-600"
        >
          📄 {msg.file_name || "Download document"}
        </a>
      );

    default:
      return (
        <p className="text-sm italic text-gray-500">
          Unsupported message
        </p>
      );
  }
};

/* =========================
   MAIN COMPONENT
========================= */
export default function SalesRepDashboard() {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [unreadCount, setUnreadCount] = useState({});
  const [leadStatus, setLeadStatus] = useState("new");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isRecording, setIsRecording] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const mediaStreamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showChat, setShowChat] = useState(false);
  const [leadSource, setLeadSource] = useState("unknown");

  
  const timerRef = useRef(null);

  const startTimer = () => {
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime((t) => t + 1);
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const formatTimer = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };


  const activeLeadRef = useRef(null);
  const chatEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    if (!selectedLead) return;

    setIsRecording(true);
    setIsLocked(false);
    startTimer();

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream; // ✅ STORE STREAM SAFELY

    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "audio/webm",
    });

    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      audioChunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      stopTimer();
      setIsRecording(false);

      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      const file = new File([audioBlob], "voice.webm", {
        type: "audio/webm",
      });

      setRecordedAudio(file);
    };

    mediaRecorder.start();
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current.stop();

    // ✅ STOP MIC PROPERLY
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };


  const sendRecordedAudio = async () => {
    if (!recordedAudio || !selectedLead) return;

    const clientId = `c-${Date.now()}`;

    const tempMessage = {
      id: clientId,
      client_id: clientId,
      lead_id: selectedLead.id,
      message_type: "voice",
      direction: "OUT",
      status: "sent",
      created_at: new Date().toISOString(),
      media_url: URL.createObjectURL(recordedAudio),
    };

    setMessages((prev) => [...prev, tempMessage]);

    const formData = new FormData();
    formData.append("file", recordedAudio);
    formData.append("lead_id", selectedLead.id);
    formData.append("client_id", clientId);   // ✅ IMPORTANT

    await api.post("/api/leads/send-media", formData);

    setRecordedAudio(null);
    setIsLocked(false);
  };



  const cancelRecording = () => {
    stopTimer();
    setIsRecording(false);
    setIsLocked(false);
    audioChunksRef.current = [];

    mediaRecorderRef.current?.stop();
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };




  const lockRecording = () => {
    setIsLocked(true);
  };


  




  /* =========================
     FETCH LEADS
  ========================= */
  useEffect(() => {
    api.get("/api/leads").then((res) => {
      setLeads(res.data || []);
      if (res.data?.length) openChat(res.data[0]);
    });
  }, []);

  /* =========================
     OPEN CHAT
  ========================= */
  // const openChat = async (lead) => {
  //   setSelectedLead(lead);
  //   setLeadStatus(lead.status || "new");
  //   activeLeadRef.current = lead.id;

  //   setUnreadCount((prev) => ({ ...prev, [lead.id]: 0 }));

  //   const res = await api.get(
  //     `/api/leads/messages/${lead.id}?page=1&limit=200`
  //   );
  //   setMessages(res.data?.data || []);
  // };
  const openChat = async (lead) => {
    setSelectedLead(lead);
    setLeadStatus(lead.status || "new");
    setLeadSource(lead.source || "unknown"); // ✅ ADD THIS
    activeLeadRef.current = lead.id;
    setUnreadCount((prev) => ({ ...prev, [lead.id]: 0 }));
  
    const res = await api.get(
      `/api/leads/messages/${lead.id}?page=1&limit=200`
    );
    setMessages(res.data?.data || []);
  
    setShowChat(true);
  };
  
  /* =========================
     UPDATE LEAD STATUS
  ========================= */
  const updateLeadStatus = async (status) => {
    setLeadStatus(status);
    await api.patch(`/api/leads/${selectedLead.id}/status`, { status });

    setLeads((prev) =>
      prev.map((l) =>
        l.id === selectedLead.id ? { ...l, status } : l
      )
    );
  };

  const updateLeadSource = async (source) => {
    setLeadSource(source);
  
    await api.patch(`/api/leads/${selectedLead.id}/source`, { source });
  
    // keep leads list in sync
    setLeads((prev) =>
      prev.map((l) =>
        l.id === selectedLead.id ? { ...l, source } : l
      )
    );
  };
  
  /* =========================
     SOCKET EVENTS
  ========================= */
  useEffect(() => {
    let socket = getSocket();
    if (!socket) socket = connectSocket();
    socket.on("newMessage", (msg) => {
      const leadId = msg.lead_id;

      // 🔒 OUT messages: ONLY replace temp, never append
      if (msg.direction === "OUT") {
        setMessages((prev) => {
          const hasTemp = prev.some(
            (m) => m.client_id && m.client_id === msg.client_id
          );

          if (!hasTemp) return prev; // ⛔ ignore duplicate OUT

          return prev.map((m) =>
            m.client_id === msg.client_id ? msg : m
          );
        });
        return;
      }

      // ✅ IN messages (customer)
      if (Number(leadId) === Number(activeLeadRef.current)) {
        setMessages((prev) => [...prev, msg]);
        return;
      }

      // notifications
      const now = Date.now();
      if (now - lastSoundAt > 1500) {
        notificationSound.play().catch(() => { });
        lastSoundAt = now;
      }

      setUnreadCount((prev) => ({
        ...prev,
        [leadId]: (prev[leadId] || 0) + 1,
      }));
    });



    socket.on("messageDelivered", ({ whatsapp_message_id }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.whatsapp_message_id === whatsapp_message_id
            ? { ...m, status: "delivered" }
            : m
        )
      );
    });

    return () => {
      socket.off("newMessage");
      socket.off("messageDelivered");
    };
  }, []);

  /* =========================
     AUTO SCROLL
  ========================= */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* =========================
     SEND MESSAGE
  ========================= */
  const sendMessage = async () => {
    if (!messageInput.trim()) return;

    const clientId = `c-${Date.now()}`;

    const tempMessage = {
      id: clientId,
      client_id: clientId,           // ✅ ADD THIS
      lead_id: selectedLead.id,
      message: messageInput,
      message_type: "text",
      direction: "OUT",
      status: "sent",
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempMessage]);
    setMessageInput("");

    await api.post("/api/leads/send", {
      message: messageInput,
      lead_id: selectedLead.id,
      client_id: clientId,            // ✅ SEND TO BACKEND
    });
  };


  const filteredLeads =
    statusFilter === "all"
      ? leads
      : leads.filter((l) => l.status === statusFilter);

  /* =========================
     UI
  ========================= */

    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* LEADS */}
      {/* LEADS */}
<div
  className={`
    bg-white border-r flex flex-col
    w-full md:w-80
    ${showChat ? "hidden md:flex" : "flex"}
  `}
>
        <div className="px-4 py-4 border-b flex justify-between">
          <h2 className="font-semibold text-lg">Leads</h2>
          <button onClick={handleLogout} className="text-xs text-red-500">
            Logout
          </button>
        </div>

        <div className="px-4 py-2 flex gap-2 flex-wrap">
          {["all", "new", "interested", "converted", "lost"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1 rounded-full ${statusFilter === s
                ? "bg-blue-600 text-white"
                : "bg-gray-100"
                }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredLeads.map((lead) => {
            const unread = unreadCount[lead.id] || 0;

            return (
              <div
                key={lead.id}
                onClick={() => openChat(lead)}
                className={`px-4 py-3 cursor-pointer border-b hover:bg-gray-100
                  ${unread > 0 ? "border-l-4 border-blue-600 bg-blue-50" : ""}
                `}
              >
                <div className="flex justify-between items-center">
                  <p className="font-medium">Lead #{lead.id}</p>
                  {unread > 0 && (
                    <span className="bg-blue-600 text-white text-xs px-2 rounded-full">
                      {unread}
                    </span>
                  )}
                </div>

                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${getStatusStyle(
                    lead.status
                  )}`}
                >
                  {lead.status || "new"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* CHAT */}
      {selectedLead && (
     <div
     className={`
       flex-1 flex flex-col
       w-full
       ${showChat ? "flex" : "hidden md:flex"}
     `}
   >
    
    <div className="px-4 py-3 bg-white border-b flex items-center justify-between">
  <div className="flex items-center gap-2">
    {/* MOBILE BACK */}
    <button
      onClick={() => setShowChat(false)}
      className="md:hidden text-xl"
    >
      ←
    </button>

    <div>
      <p className="font-semibold">Lead #{selectedLead.id}</p>
      <span className="text-xs text-gray-500">
        Status: {leadStatus}
      </span>
    </div>
  </div>

  <div className="flex items-center gap-2">
  {/* STATUS */}
  <select
    value={leadStatus}
    onChange={(e) => updateLeadStatus(e.target.value)}
    className="text-xs border rounded px-2 py-1"
  >
    <option value="new">New</option>
    <option value="interested">Interested</option>
    <option value="converted">Converted</option>
    <option value="lost">Lost</option>
  </select>

  {/* SOURCE */}
  <select
    value={leadSource}
    onChange={(e) => updateLeadSource(e.target.value)}
    className="text-xs border rounded px-2 py-1 bg-gray-50"
  >
    <option value="unknown">Unknown</option>
    <option value="ad">Ad</option>
    <option value="organic">Organic</option>
    <option value="referral">Referral</option>
  </select>
</div>

          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-100">
            {messages.map((msg, i) => {
              const msgDate = getMessageDate(msg);
              const prev = messages[i - 1];
              const prevDate = prev ? getMessageDate(prev) : null;

              const showDate =
                i === 0 || !isSameDay(msgDate, prevDate);

              return (
                <React.Fragment key={msg.id}>
                  {showDate && (
                    <div className="flex justify-center my-4">
                      <span className="bg-gray-300 text-xs px-4 py-1 rounded-full">
                        {getDateLabel(msgDate)}
                      </span>
                    </div>
                  )}

                  <div
                    className={`mb-2 flex ${msg.direction === "IN"
                      ? "justify-start"
                      : "justify-end"
                      }`}
                  >
                    <div
                      className={`max-w-md px-4 py-2 rounded-2xl ${msg.direction === "IN"
                        ? "bg-white"
                        : "bg-blue-600 text-white"
                        }`}
                    >
                      <p className="text-xs font-semibold mb-1">
                        {msg.direction === "IN" ? "Customer" : "You"}
                      </p>

                      {renderMessageContent(msg)}

                      <p className="text-[10px] mt-1 opacity-60 text-right flex items-center gap-1">
                        {formatTime(msgDate)}

                        {msg.direction === "OUT" && (
                          <span>
                            {msg.status === "sent" && "✓"}
                            {msg.status === "delivered" && "✓✓"}
                            {msg.status === "read" && "✓✓"}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={chatEndRef} />
          </div>
          <div className="px-3 py-2 bg-white border-t">
            {isRecording ? (
              /* RECORDING */
              <div className="flex items-center gap-3 bg-gray-100 rounded-full px-4 py-2 h-11">
                <span className="w-2.5 h-2.5 bg-red-600 rounded-full animate-pulse" />

                <span className="text-xs font-mono w-12 text-center">
                  {formatTimer(recordingTime)}
                </span>

                <span className="text-xs text-gray-500 flex-1">
                  Recording…
                </span>

                {!isLocked ? (
                  <>
                    <button onClick={lockRecording}>🔒</button>
                    <button onClick={cancelRecording}>❌</button>
                  </>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center"
                  >
                    ➤
                  </button>
                )}
              </div>

            ) : recordedAudio ? (
              /* AUDIO READY (WHATSAPP STYLE) */
              <div className="flex items-center gap-3 bg-gray-100 rounded-full px-4 py-2 h-11">
                <span className="text-xs text-gray-600 flex-1">
                  Voice message ready
                </span>

                <button
                  onClick={sendRecordedAudio}
                  className="w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center"
                >
                  ➤
                </button>

                <button
                  onClick={() => setRecordedAudio(null)}
                  className="text-red-500"
                >
                  ❌
                </button>
              </div>

            ) : (
              /* NORMAL INPUT */
              <div className="flex items-center gap-2">
                {/* 📎 ATTACHMENT */}
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"
                >
                  📎
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file || !selectedLead) return;

                    const clientId = `c-${Date.now()}`;

                    const type = file.type.startsWith("image")
                      ? "image"
                      : file.type.startsWith("video")
                        ? "video"
                        : "document";

                    // TEMP MESSAGE (instant UI)
                    setMessages((prev) => [
                      ...prev,
                      {
                        id: clientId,
                        client_id: clientId,
                        lead_id: selectedLead.id,
                        message_type: type,
                        direction: "OUT",
                        status: "sent",
                        created_at: new Date().toISOString(),
                        media_url: URL.createObjectURL(file),
                        file_name: file.name,
                      },
                    ]);

                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("lead_id", selectedLead.id);
                    formData.append("client_id", clientId);

                    await api.post("/api/leads/send-media", formData);

                    e.target.value = "";
                  }}
                />

                <input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  className="flex-1 bg-gray-100 rounded-full px-4 py-2 h-10"
                  placeholder="Type a message"
                />


                {messageInput.trim() ? (
                  <button
                    onClick={sendMessage}
                    className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center"
                  >
                    ➤
                  </button>
                ) : (
                  <button
                    onMouseDown={startRecording}
                    className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center"
                  >
                    🎤
                  </button>
                )}
              </div>
            )}

          </div>



        </div>
      )}
    </div>
  );
}
